import Card from '../../components/Card';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useT } from '../../utils/useTranslation';
import { useDatabase } from '../../context/DatabaseContext';
import { useAuth } from '../../context/AuthContext';
import heroBg from '../../assets/sas_hero_new.png';
import { motion, AnimatePresence } from 'framer-motion';
import HeroBackground from '../../components/ui/HeroBackground';
import ajrakTexture from '../../assets/ajrak_texture_new.jpg';
import { doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../../firebase';
import toast from 'react-hot-toast';

export default function EventDetailPage() {
  const t = useT();
  const { id } = useParams();
  const navigate = useNavigate();
  const { getDocument } = useDatabase();
  const { user } = useAuth();
  const [item, setItem] = useState(null);
  const [branch, setBranch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newContribution, setNewContribution] = useState({ type: 'Poetry', content: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const hasContributed = item?.contributions?.some(c => c.memberId === user?.id);
  const isMember = user?.role === 'member';

  const canEdit = (createdAt) => {
    if (!createdAt) return false;
    const diff = (new Date() - new Date(createdAt)) / (1000 * 60 * 60);
    const eventTime = item?.time || '00:00';
    const eventDateTime = new Date(`${item?.date}T${eventTime}`);
    const isPastEventDeadline = (eventDateTime - new Date()) < (60 * 60 * 1000);
    return diff < 2 && !isPastEventDeadline;
  };

  const isPastEventDeadline = () => {
    if (!item?.date) return false;
    const eventTime = item.time || '00:00';
    const eventDateTime = new Date(`${item.date}T${eventTime}`);
    const now = new Date();
    return (eventDateTime - now) < (60 * 60 * 1000);
  };

  const updateContribution = async (contribId) => {
    const contribution = item.contributions.find(c => c.id === contribId);
    if (isMember && !canEdit(contribution?.createdAt)) {
      toast.error(isRTL ? 'تبديلي جو وقت ختم ٿي چڪو آهي.' : 'Edit time expired.');
      return;
    }
    try {
      const updated = item.contributions.map(c => 
        c.id === contribId ? { ...c, content: editContent, updatedAt: new Date().toISOString() } : c
      );
      await updateDoc(doc(db, 'events', id), { contributions: updated });
      setItem(prev => ({ ...prev, contributions: updated }));
      setEditingId(null);
      toast.success(isRTL ? 'تبديلي محفوظ ٿي وئي!' : 'Update saved!');
    } catch (err) {
      toast.error('Error updating');
    }
  };


  useEffect(() => {
    const fetchItem = async () => {
      try {
        const data = await getDocument('events', id);
        if (data) {
          setItem(data);
          if (data.branch) {
            const bData = await getDocument('branches', data.branch);
            if (bData) setBranch(bData);
          }
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchItem();
  }, [id, getDocument]);
  const typeIcon = t => ({
    event: '📅',
    news: '📰',
    announcement: '📢'
  })[t] || '📄';
  const typeColor = t => ({
    event: 'navy',
    news: 'green',
    announcement: 'red'
  })[t] || 'navy';
  const isRTL = t.language === 'sindhi';

  if (loading) return <div>
    <div className="hero-premium">
      <HeroBackground />
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '60vh',
        position: 'relative',
        zIndex: 10
      }}>
        <div className="spinner" />
      </div>
    </div>
  </div>;

  const isAuthorized = item?.isGlobal === true || (user && (user.role === 'main_admin' || user.branchId === item?.branch || (user.role === 'branch_admin' && (!user.branchId || item?.branch === ''))));

  if (!item || !isAuthorized) return <div>
    <div className="hero-premium">
      <HeroBackground />
      <div style={{
        padding: '100px 2rem',
        color: 'white',
        textAlign: 'center',
        position: 'relative',
        zIndex: 10,
        direction: isRTL ? 'rtl' : 'ltr'
      }}>
        <h1 style={{
          fontFamily: isRTL ? 'var(--font-sindhi)' : 'var(--font-display)',
          fontSize: 40,
          fontWeight: 800
        }}>{t('eventNotFound')}</h1>
      </div>
    </div>
    <div style={{
      background: 'var(--sindhi-ivory)',
      backgroundImage: `url(${ajrakTexture})`,
      backgroundSize: '600px 600px',
      backgroundRepeat: 'repeat',
      position: 'relative',
      zIndex: 10,
      marginTop: '-40px'
    }}>
      <div style={{
        maxWidth: 600,
        margin: '0 auto',
        textAlign: 'center',
        padding: '60px 24px 100px',
        position: 'relative',
        zIndex: 1
      }}>
        <p style={{
          color: 'var(--sindhi-text-muted)',
          fontSize: 18,
          marginBottom: 32,
          fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit'
        }}>{t('eventNotFoundDesc')}</p>
        <button className="btn btn-primary" onClick={() => navigate('/events')}>{t('backToEvents')}</button>
      </div>
    </div>
  </div>;

  const displayTitle = isRTL ? (item.titleSindhi || item.title) : item.title;
  const displayDescription = isRTL ? (item.descriptionSindhi || item.description) : item.description;

  return <div>
    <div className="hero-premium">
      <HeroBackground />
      <div style={{
        maxWidth: 900,
        margin: '0 auto',
        position: 'relative',
        zIndex: 10,
        padding: '0 2rem',
        direction: isRTL ? 'rtl' : 'ltr',
        textAlign: isRTL ? 'right' : 'left'
      }}>
        <Link to="/events" style={{
          color: 'var(--sindhi-gold)',
          textDecoration: 'none',
          fontSize: 14,
          fontWeight: 700,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 24,
          fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit'
        }}>
          {isRTL ? 'واپس تقريبن ڏانهن' : t('backToEvents')}
        </Link>
        <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} style={{
          fontFamily: isRTL ? 'var(--font-sindhi)' : 'var(--font-display)',
          fontSize: isRTL ? 56 : 48,
          fontWeight: 800,
          marginBottom: 8,
          letterSpacing: -1
        }}>
          {displayTitle}
        </motion.h1>
      </div>
    </div>

    <div style={{
      background: 'var(--sindhi-ivory)',
      backgroundImage: `url(${ajrakTexture})`,
      backgroundSize: '600px 600px',
      backgroundRepeat: 'repeat',
      position: 'relative',
      zIndex: 10,
      marginTop: '-60px'
    }}>
      <div style={{
        position: 'absolute',
        inset: 0,
        backdropFilter: 'blur(3px)',
        WebkitBackdropFilter: 'blur(3px)',
        background: 'rgba(253,248,235,0.7)',
        zIndex: 0,
        pointerEvents: 'none'
      }} />
      <div style={{
        maxWidth: 900,
        margin: '0 auto',
        padding: '0 24px 100px',
        position: 'relative',
        zIndex: 1
      }}>
        <Card style={{
          overflow: 'hidden',
          background: 'white',
          direction: isRTL ? 'rtl' : 'ltr'
        }}>
          {/* Image */}
          {item.photo && <div style={{
            width: '100%',
            height: 450,
            overflow: 'hidden',
            background: 'var(--sindhi-navy)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <img src={item.photo} alt={displayTitle} style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }} />
          </div>}

          <div style={{
            padding: 48,
            textAlign: isRTL ? 'right' : 'left'
          }}>
            {/* Badges */}
            <div style={{
              display: 'flex',
              gap: 12,
              alignItems: 'center',
              marginBottom: 32,
              flexWrap: 'wrap',
              justifyContent: isRTL ? 'flex-start' : 'flex-start'
            }}>
              <span className={`badge`} style={{
                background: `var(--sindhi-${typeColor(item.type)})`,
                color: 'white',
                padding: '8px 16px',
                borderRadius: 10,
                fontSize: 11,
                fontWeight: 800,
                textTransform: 'uppercase',
                letterSpacing: 1,
                fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit'
              }}>
                {typeIcon(item.type)} {item.type === 'event' ? t('event') : item.type === 'news' ? t('news') : t('announcement')}
              </span>
              {item.isGlobal && <span style={{
                fontSize: 11,
                fontWeight: 800,
                textTransform: 'uppercase',
                background: 'var(--sindhi-ivory)',
                color: 'var(--sindhi-gold)',
                padding: '8px 16px',
                borderRadius: 10,
                letterSpacing: 1,
                fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit'
              }}>{t('global')}</span>}
              {branch && <span style={{
                fontSize: 11,
                fontWeight: 800,
                textTransform: 'uppercase',
                background: 'var(--sindhi-ivory)',
                color: 'var(--sindhi-navy)',
                padding: '8px 16px',
                borderRadius: 10,
                letterSpacing: 1,
                fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit'
              }}>{isRTL ? (branch.nameSindhi || branch.name) : branch.name}</span>}
            </div>

            {/* Meta Info Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: 24,
              marginBottom: 40,
              background: 'var(--sindhi-ivory)',
              padding: 24,
              borderRadius: 20
            }}>
              {item.date && <div>
                <div style={{
                  fontSize: 11,
                  fontWeight: 800,
                  color: 'var(--sindhi-text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: 1,
                  marginBottom: 8,
                  fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit'
                }}>{t('date')}</div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  color: 'var(--sindhi-navy)',
                  fontWeight: 800
                }}>
                  <span style={{
                    fontSize: 20
                  }}>📅</span>
                  <span>{new Date(item.date).toLocaleDateString(isRTL ? 'sd-PK' : 'en-PK', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    weekday: 'long'
                  })}</span>
                </div>
              </div>}
              {item.venue && <div>
                <div style={{
                  fontSize: 11,
                  fontWeight: 800,
                  color: 'var(--sindhi-text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: 1,
                  marginBottom: 8,
                  fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit'
                }}>{t('venue')}</div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  color: 'var(--sindhi-navy)',
                  fontWeight: 800,
                  fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit'
                }}>
                  <span style={{
                    fontSize: 20
                  }}>🏢</span>
                  <span>{item.venue}</span>
                </div>
              </div>}
              {branch && <div>
                <div style={{
                  fontSize: 11,
                  fontWeight: 800,
                  color: 'var(--sindhi-text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: 1,
                  marginBottom: 8,
                  fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit'
                }}>{t('locationLabel')}</div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  color: 'var(--sindhi-navy)',
                  fontWeight: 800,
                  fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit'
                }}>
                  <span style={{
                    fontSize: 20
                  }}>📍</span>
                  <span>{isRTL ? (branch.citySindhi || branch.city) : branch.city}, {isRTL ? (branch.countrySindhi || branch.country) : branch.country}</span>
                </div>
              </div>}
            </div>

            {/* Divider */}
            <div style={{
              height: 1,
              background: 'var(--sindhi-cream)',
              margin: '40px 0'
            }} />

            {/* Description */}
            {displayDescription && <div>
              <h2 style={{
                fontSize: 13,
                fontWeight: 800,
                color: 'var(--sindhi-text-muted)',
                textTransform: 'uppercase',
                letterSpacing: 1,
                marginBottom: 20,
                fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit'
              }}>
                {t('description')}
              </h2>
              <div style={{
                fontSize: 16,
                lineHeight: 1.9,
                color: 'var(--sindhi-text)',
                whiteSpace: 'pre-wrap',
                opacity: 0.9,
                fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit'
              }}>
                {displayDescription}
              </div>
            </div>}

            {/* Contributions Section */}
            {item.branch && (
              <div style={{ marginTop: 60 }}>
                <h2 style={{
                  fontSize: 24,
                  fontWeight: 800,
                  color: 'var(--sindhi-navy)',
                  marginBottom: 24,
                  fontFamily: isRTL ? 'var(--font-sindhi)' : 'var(--font-display)',
                  borderBottom: '2px solid var(--sindhi-cream)',
                  paddingBottom: 12
                }}>
                  {isRTL ? 'ميمبرن جي ڀائيواري (تخليقون)' : 'Member Contributions'}
                </h2>

                {/* Display Contributions */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20, marginBottom: 40 }}>
                  {(!item.contributions || item.contributions.length === 0) ? (
                    <div style={{ color: 'var(--sindhi-text-muted)', fontStyle: 'italic', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
                      {isRTL ? 'اڃا تائين ڪا به تخليق شامل نه ڪئي وئي آهي.' : 'No contributions yet. Be the first to contribute!'}
                    </div>
                  ) : (
                    item.contributions.map((c, i) => (
                      <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} style={{
                        background: 'var(--sindhi-ivory)',
                        padding: 24,
                        borderRadius: 16,
                        border: '1px solid var(--sindhi-cream)'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                          <span style={{ fontWeight: 800, color: 'var(--sindhi-navy)', fontSize: 16, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{c.memberName}</span>
                          <span style={{ fontSize: 11, background: 'var(--sindhi-maroon)', color: 'white', padding: '4px 10px', borderRadius: 100, fontWeight: 700 }}>{c.type}</span>
                        </div>
                        <div style={{ fontSize: 15, color: 'var(--sindhi-text)', whiteSpace: 'pre-wrap', lineHeight: 1.8, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
                          {editingId === c.id ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                              <textarea className="form-input" value={editContent} onChange={e => setEditContent(e.target.value)} rows={4} style={{ fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }} />
                              <div style={{ display: 'flex', gap: 8 }}>
                                <button onClick={() => updateContribution(c.id)} style={{ padding: '6px 12px', background: 'var(--sindhi-navy)', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 700 }}>{isRTL ? 'محفوظ ڪريو' : 'Save'}</button>
                                <button onClick={() => setEditingId(null)} style={{ padding: '6px 12px', background: 'var(--sindhi-cream)', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 700 }}>{isRTL ? 'منسوخ' : 'Cancel'}</button>
                              </div>
                            </div>
                          ) : c.content}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
                          <div style={{ fontSize: 11, color: 'var(--sindhi-text-muted)' }}>
                            {new Date(c.createdAt).toLocaleDateString()} {c.updatedAt && (isRTL ? '(تبديل ٿيل)' : '(Edited)')}
                          </div>
                          {user?.id === c.memberId && (
                            <div style={{ display: 'flex', gap: 8 }}>
                              {canEdit(c.createdAt) && (
                                <button onClick={() => { setEditingId(c.id); setEditContent(c.content); }} style={{ background: 'none', border: 'none', color: 'var(--sindhi-navy)', fontSize: 12, cursor: 'pointer', fontWeight: 700, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
                                  {isRTL ? 'تبديل ڪريو' : 'Edit'}
                                </button>
                              )}
                              {(user.role === 'main_admin' || user.role === 'branch_admin' || (isMember && canEdit(c.createdAt))) && (
                                <button onClick={async (e) => {
                                  e.preventDefault();
                                  if (!window.confirm(isRTL ? 'توهان پڪ آهيو ته هن ڀائيواري کي ڊاهڻ چاهيو ٿا؟' : 'Are you sure you want to delete this contribution?')) return;
                                  try {
                                    const updatedContributions = item.contributions.filter(contrib => contrib.id !== c.id);
                                    await updateDoc(doc(db, 'events', id), { contributions: updatedContributions });
                                    setItem(prev => ({ ...prev, contributions: updatedContributions }));
                                    toast.success(isRTL ? 'تخليق ڊاهجي وئي.' : 'Contribution deleted.');
                                  } catch (err) {
                                    console.error(err);
                                    toast.error(isRTL ? 'غلطي ٿي.' : 'Error deleting contribution.');
                                  }
                                }} style={{ background: 'none', border: 'none', color: '#e11d48', fontSize: 12, cursor: 'pointer', fontWeight: 700, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
                                  {isRTL ? 'ڊاهيو' : 'Delete'}
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </motion.div>
                    ))
                  )}
                </div>

                {/* Add Contribution Form */}
                {(user && user.branchId === item.branch) && (
                  <div style={{ background: 'white', border: '1px solid var(--sindhi-cream)', padding: 32, borderRadius: 20, boxShadow: '0 10px 30px -10px rgba(0,0,0,0.05)' }}>
                    <h3 style={{ fontSize: 18, fontWeight: 800, color: 'var(--sindhi-navy)', marginBottom: 20, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
                      {isRTL ? 'پنهنجي تخليق شامل ڪريو' : 'Add Your Contribution'}
                    </h3>
                    {isPastEventDeadline() ? (
                      <div style={{ background: 'var(--sindhi-ivory)', padding: 24, borderRadius: 12, textAlign: 'center', border: '1px solid #feecef' }}>
                        <span style={{ fontSize: 24, display: 'block', marginBottom: 12 }}>⏰</span>
                        <p style={{ margin: 0, color: '#e11d48', fontWeight: 700, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
                          {isRTL ? 'هن تقريب لاءِ لکڻيون شامل ڪرڻ جو وقت ختم ٿي چڪو آهي.' : 'The time to add writings for this event has expired.'}
                        </p>
                      </div>
                    ) : isMember && hasContributed ? (
                      <div style={{ background: 'var(--sindhi-ivory)', padding: 24, borderRadius: 12, textAlign: 'center', border: '1px solid var(--sindhi-gold)' }}>
                        <span style={{ fontSize: 24, display: 'block', marginBottom: 12 }}>✍️</span>
                        <p style={{ margin: 0, color: 'var(--sindhi-navy)', fontWeight: 700, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
                          {isRTL ? 'توهان اڳ ۾ ئي هن تقريب لاءِ پنهنجي تخليق شامل ڪري چڪا آهيو.' : 'You have already added your contribution to this event.'}
                        </p>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: 8, fontSize: 13, fontWeight: 700, color: 'var(--sindhi-text-muted)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{isRTL ? 'تخليق جو قسم' : 'Contribution Type'}</label>
                        <select className="form-input" value={newContribution.type} onChange={e => setNewContribution({ ...newContribution, type: e.target.value })} style={{ fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
                          <option value="Nazam">{isRTL ? 'نظم' : 'Nazam'}</option>
                          <option value="Poetry">{isRTL ? 'شاعري' : 'Poetry'}</option>
                          <option value="Geet">{isRTL ? 'گيت' : 'Geet'}</option>
                          <option value="Story">{isRTL ? 'ڪهاڻي' : 'Story'}</option>
                          <option value="Other">{isRTL ? 'ٻيو' : 'Other'}</option>
                        </select>
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: 8, fontSize: 13, fontWeight: 700, color: 'var(--sindhi-text-muted)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{isRTL ? 'تخليق' : 'Content'}</label>
                        <textarea className="form-input" rows={5} value={newContribution.content} onChange={e => setNewContribution({ ...newContribution, content: e.target.value })} placeholder={isRTL ? 'هتي لکو...' : 'Write here...'} style={{ fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }} />
                      </div>
                        <button 
                        className="btn btn-primary" 
                        disabled={isSubmitting || !newContribution.content.trim()} 
                        onClick={async (e) => {
                          e.preventDefault();
                          if (!newContribution.content.trim()) return;
                          if (isPastEventDeadline()) {
                            toast.error(isRTL ? 'وقت ختم ٿي چڪو آهي.' : 'Deadline passed.');
                            return;
                          }
                          if (isMember && hasContributed) {
                            toast.error(isRTL ? 'توهان اڳ ۾ ئي هڪ تخليق شامل ڪري چڪا آهيو.' : 'You have already added a contribution.');
                            return;
                          }
                          setIsSubmitting(true);
                          try {
                            const newEntry = {
                              id: new Date().getTime().toString() + Math.random().toString(36).substring(2, 5),
                              memberId: user.id || 'unknown',
                              memberName: user.displayName || user.full_name || user.email || 'Member',
                              type: newContribution.type,
                              content: newContribution.content.trim(),
                              createdAt: new Date().toISOString()
                            };
                            await updateDoc(doc(db, 'events', id), {
                              contributions: arrayUnion(newEntry)
                            });
                            setItem(prev => ({
                              ...prev,
                              contributions: [...(prev.contributions || []), newEntry]
                            }));
                            setNewContribution({ type: 'Poetry', content: '' });
                            toast.success(isRTL ? 'شامل ٿي ويو!' : 'Contribution added!');
                          } catch (err) {
                            console.error('Submit contribution error:', err);
                            toast.error(isRTL ? 'غلطي ٿي!' : 'Failed to add. Please try again.');
                          } finally {
                            setIsSubmitting(false);
                          }
                        }}
                        style={{ alignSelf: isRTL ? 'flex-end' : 'flex-start' }}
                      >
                        {isSubmitting ? <span className="spinner" style={{ width: 20, height: 20, borderTopColor: 'white' }} /> : (isRTL ? 'شامل ڪريو' : 'Submit Contribution')}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Footer */}
            <div style={{
              marginTop: 48,
              paddingTop: 24,
              borderTop: '1px solid var(--sindhi-cream)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontSize: 13,
              color: 'var(--sindhi-text-muted)'
            }}>
              <span style={{ fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{t('postedOn')} {new Date(item.createdAt || item.date).toLocaleDateString(isRTL ? 'sd-PK' : 'en-PK', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              })}</span>
              <span style={{
                fontWeight: 800,
                color: 'var(--sindhi-maroon)',
                fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit'
              }}>{isRTL ? 'سنڌي ادبي سنگت' : 'Sindhi Adabi Sangat'}</span>
            </div>
          </div>
        </Card>
      </div>
    </div>
  </div>;
}